import { useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { FadeScrollArea } from "@/components/common/FadeScrollArea";
import { useAuth } from "@/contexts/AuthContext";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingBuildPipeline } from "./useManufacturingBuildPipeline";
import { BuildPipelineColumn } from "./ManufacturingBuildPipelineBoard";
import { ManufacturingBuildPipelinePhaseEditor } from "./ManufacturingBuildPipelinePhaseEditor";
import { BuildPipelineLoadsStrip } from "./BuildPipelineLoadsStrip";
import { BuildPipelineOverviewStats } from "./BuildPipelineOverviewStats";
import { PlantMaterialRequestPopover } from "./PlantMaterialRequestPopover";
import { useManufacturingMaterials } from "./useManufacturingMaterials";
import {
  ManufacturingBuildPipelineViewToggle,
  type BuildPipelineView,
} from "./ManufacturingBuildPipelineViewToggle";
import { ManufacturingUnitsRoster } from "./ManufacturingUnitsRoster";
import { BuildPipelineUnitDetailDrawer } from "./BuildPipelineUnitDetailDrawer";
import { isPlantManagerProductionPipelineScope } from "@/lib/manufacturing/buildPipelinePhaseAccess";
import type { MfgUnitSalePath } from "@/lib/manufacturing/types";
import {
  parseBuildPipelineSearchTerms,
  unitMatchesBuildPipelineSearch,
} from "./buildPipelineUnitSearch";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

const SALE_OPTIONS: Array<{ value: "all" | MfgUnitSalePath; label: string }> = [
  { value: "all", label: "All sale paths" },
  { value: "inventory_no_sale_yet", label: "Stock" },
  { value: "cash", label: "Cash" },
  { value: "finance", label: "Finance" },
  { value: "rto", label: "RTO" },
];

export function ManufacturingBuildPipelinePanel() {
  const gate = useManufacturingEntitlementGate();
  const auth = useAuth();
  const actorRoles = auth.roles ?? [];
  const productionScoped = isPlantManagerProductionPipelineScope(actorRoles);
  const [navTick, setNavTick] = useState(0);
  const [view, setView] = useState<BuildPipelineView>("kanban");
  const [boardSearch, setBoardSearch] = useState("");
  const [requestOpen, setRequestOpen] = useState(false);
  const m = useManufacturingBuildPipeline(navTick, actorRoles);
  const requestMaterials = useManufacturingMaterials({
    enabled: gate.enabled && requestOpen,
  });
  const canEditPhases = auth.hasRole(
    "manufacturing",
    "manufacturing_plant_supervisor",
    "admin",
    "owner",
    "org_admin",
    "org_owner",
    "platform_owner",
    "platform_admin",
    "platform_dev",
  );

  const searchTerms = useMemo(() => parseBuildPipelineSearchTerms(boardSearch), [boardSearch]);
  const filteredColumns = useMemo(
    () =>
      m.columns.map((col) => ({
        ...col,
        units: col.units.filter((u) => unitMatchesBuildPipelineSearch(u, searchTerms)),
      })),
    [m.columns, searchTerms],
  );

  useEffect(() => {
    if (!m.toast) return;
    const t = setTimeout(() => m.setToast(null), 3200);
    return () => clearTimeout(t);
  }, [m.toast, m.setToast]);

  useEffect(() => {
    if (!canEditPhases && view === "phaseSettings") setView("kanban");
  }, [canEditPhases, view]);

  const showKanbanChrome = view === "kanban";

  return (
    <div className="w-full space-y-4 text-left">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">Build pipeline</h2>
        {showKanbanChrome ? (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
              Plant
              <select
                className={inputClass}
                value={m.plantId}
                onChange={(e) => {
                  m.setPlantId(e.target.value);
                  m.setSelectedUnitId(null);
                }}
                disabled={m.listBusy || m.plants.length === 0}
              >
                {m.plants.length === 0 ? <option value="">No plants</option> : null}
                {m.plants.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.plant_code} · {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
              Sale path
              <select
                className={inputClass}
                value={m.salePathFilter}
                onChange={(e) =>
                  m.setSalePathFilter(e.target.value as "all" | MfgUnitSalePath)
                }
                disabled={m.listBusy}
              >
                {SALE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={m.listBusy}
              onClick={() => setNavTick((n) => n + 1)}
            >
              Refresh
            </button>
          </div>
        ) : null}
      </div>

      <ManufacturingGateBanner gate={gate} />

      {showKanbanChrome && m.localError ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {m.localError}
        </p>
      ) : null}

      {showKanbanChrome && m.overview ? (
        <BuildPipelineOverviewStats
          inProgress={m.overview.in_progress}
          finalQa={m.overview.final_qa}
          buildComplete={m.overview.build_complete}
          onBoard={m.overview.total_active}
          onInventoryRequest={() => setRequestOpen(true)}
        />
      ) : null}

      <SectionCard
        title={
          <ManufacturingBuildPipelineViewToggle
            view={view}
            onChange={setView}
            showPhaseSettings={canEditPhases}
            productionScoped={productionScoped}
          />
        }
      >
        {view === "phaseSettings" && canEditPhases ? (
          <ManufacturingBuildPipelinePhaseEditor
            phases={m.phases}
            onChanged={async () => {
              await m.reload();
            }}
          />
        ) : view === "roster" ? (
          <ManufacturingUnitsRoster />
        ) : (
          <>
            <label className="mb-3 block text-sm text-gray-700 dark:text-zinc-300">
              <span className="sr-only">Search board</span>
              <input
                className={`${inputClass} w-full max-w-xl`}
                value={boardSearch}
                onChange={(e) => setBoardSearch(e.target.value)}
                placeholder="Search serial, customer, location… (comma for multiple)"
                disabled={m.listBusy}
              />
            </label>
            <BuildPipelineLoadsStrip
              loads={m.loads}
              busy={m.listBusy || m.loadBusy}
              aliasDraft={m.loadAlias}
              onAliasDraft={m.setLoadAlias}
              footageDraft={m.loadFootage}
              onFootageDraft={m.setLoadFootage}
              onCreate={() => void m.createLoad()}
              onDispatch={(id) => void m.dispatchLoad(id)}
              onClose={(id) => void m.closeLoad(id)}
            />
            {m.listBusy && m.columns.length === 0 ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : m.columns.length === 0 ? (
              <p className="text-sm text-gray-500">No phases configured for this org.</p>
            ) : (
              <FadeScrollArea axis="horizontal" contentClassName="pb-2">
                <div className="flex w-max gap-3">
                  {filteredColumns.map((col) => (
                    <BuildPipelineColumn
                      key={col.phase.id}
                      phase={col.phase}
                      units={col.units}
                      phases={m.phases}
                      loads={m.loads}
                      actorRoles={actorRoles}
                      moveBusyId={m.moveBusyId}
                      dragOver={m.dragOverPhaseId === col.phase.id}
                      onOpenUnit={(id) => m.setSelectedUnitId(id)}
                      onDragStartUnit={(id) => m.setDragUnitId(id)}
                      onDragEndUnit={() => {
                        m.setDragUnitId(null);
                        m.setDragOverPhaseId(null);
                      }}
                      onDropUnit={m.dropOnPhase}
                      onDragOverPhase={(id) => m.setDragOverPhaseId(id)}
                      onDragLeavePhase={(id) => {
                        if (m.dragOverPhaseId === id) m.setDragOverPhaseId(null);
                      }}
                    />
                  ))}
                </div>
              </FadeScrollArea>
            )}
            <button
              type="button"
              className={`${mfgPrimaryBtnClass} mt-3`}
              disabled={m.listBusy}
              onClick={() => void m.reload()}
            >
              Reload board
            </button>
          </>
        )}
      </SectionCard>

      {m.selectedUnit ? (
        <BuildPipelineUnitDetailDrawer
          unit={m.selectedUnit}
          phases={m.phases}
          openLoads={m.loads.filter((l) => l.status === "open")}
          actorRoles={actorRoles}
          moveBusy={m.moveBusyId === m.selectedUnit.id}
          loadBusy={m.loadBusy}
          onClose={() => m.setSelectedUnitId(null)}
          onMove={m.moveUnit}
          onAssignLoad={(loadId) => {
            const id = m.selectedUnitId;
            if (id) void m.assignUnitLoad(id, loadId);
          }}
          onIssued={() => void m.reload()}
          shipBusy={m.shipBusy}
          onShip={() => {
            const id = m.selectedUnitId;
            if (id) void m.shipUnit(id);
          }}
        />
      ) : null}

      {m.toast ? (
        <div
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-lg"
          role="status"
        >
          {m.toast}
        </div>
      ) : null}

      <PlantMaterialRequestPopover
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        enabled={gate.enabled}
        plants={m.plants}
        materials={requestMaterials.materials}
        defaultPlantId={m.plantId}
        onSubmitted={(poNumber) => {
          m.setToast(`Sent inventory request ${poNumber}. Purchasing will review.`);
        }}
      />
    </div>
  );
}
